// src/containers/pages/caseManagement/hooks/useCaseManagement.ts

import { useState, useCallback, useMemo, useRef } from 'react';
import { useNotificationProvider } from 'hooks';
import { CANtrackComment, CANtrackCase } from 'containers/pages/comments/types/comment';
import { convertToCANtrackComment } from 'containers/pages/comments/utils/convertToCANtrackComment';
import { convertToCANtrackCase } from 'containers/pages/comments/utils/convertToCANtrackCase';
import { getAllCommentsByCaseId } from 'api/cases/getAllCommentsByCaseId';
import getCase from 'api/cases/getCase';
import getActivities from 'api/comments/getActivities';
import { updateComment } from 'api/comments/updateComment';
import { autoMapActivities } from '../utils/autoMapActivities';
import {
  deriveDeleteButtonState,
  deriveDeleteBlockedTooltip,
} from '../utils/deleteButtonState';
import useCaseDeletability from './useCaseDeletability';
import type {
  SelectedCustomer,
  DestActivity,
  MoveProgress,
  Employee,
  CaseManagementState,
  CaseManagementHandler,
  CaseManagementDerived,
} from '../types';

// ──────────────────────────────────────────────────────────
// Hook
// ──────────────────────────────────────────────────────────

function useCaseManagement() {
  const { notifySuccess, notifyError, notifyWarning } =
    useNotificationProvider();

  // ─── State (individual useState per field) ────────────

  // Customer
  const [customer, setCustomer] = useState<SelectedCustomer | null>(null);

  // Source
  const [sourceCase, setSourceCase] = useState<CANtrackCase | null>(null);
  const [comments, setComments] = useState<CANtrackComment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);

  // Filter
  const [employeeFilter, setEmployeeFilter] = useState<number | null>(null);

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Destination
  const [destinationCase, setDestinationCase] =
    useState<CANtrackCase | null>(null);
  const [destinationActivities, setDestinationActivities] = useState<
    DestActivity[]
  >([]);
  const [loadingDestActivities, setLoadingDestActivities] = useState(false);

  // Activity mapping
  const [commentActivityMap, setCommentActivityMap] = useState<
    Record<number, number | null>
  >({});

  // Move execution
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState<MoveProgress>({
    done: 0,
    total: 0,
    failed: 0,
  });
  const [failedCommentIds, setFailedCommentIds] = useState<number[]>([]);

  // Ref to avoid stale closure during async move loop
  const applyingRef = useRef(false);

  // ─── Guarded case delete (design §6) ─────────────────

  // Bumped on a successful delete so the source CasePicker also clears its own
  // search selection - it owns that state, this hook cannot reach it
  // (FE-DEFECT-2, 2026-07-21: the deleted case lingered in the search field).
  const [sourceResetSignal, setSourceResetSignal] = useState(0);

  const handleSourceCaseDeleted = useCallback(() => {
    // after a successful delete the source panel clears and the button hides
    setSourceCase(null);
    setComments([]);
    setSelectedIds(new Set());
    setCommentActivityMap({});
    setFailedCommentIds([]);
    setSourceResetSignal((n) => n + 1);
  }, []);

  const {
    deletability,
    loadingDeletability,
    deleting,
    refreshDeletability,
    clearDeletability,
    executeDelete,
  } = useCaseDeletability(handleSourceCaseDeleted);

  // ─── Derived state ───────────────────────────────────

  const filteredComments = useMemo(() => {
    if (employeeFilter === null) return comments;
    return comments.filter((c) => c.consultantId === employeeFilter);
  }, [comments, employeeFilter]);

  // Unique employees derived from comments (for employee filter dropdown)
  const employees: Employee[] = useMemo(() => {
    const map = new Map<number, string>();
    for (const c of comments) {
      const id = c.consultantId;
      const name = c.consultantName;
      if (id && name && !map.has(id)) {
        map.set(id, name);
      }
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [comments]);

  // Selection helpers
  const allVisibleSelected =
    filteredComments.length > 0 &&
    filteredComments.every((c) => selectedIds.has(c.id));
  const someVisibleSelected = filteredComments.some((c) =>
    selectedIds.has(c.id),
  );

  // ─── Validation ──────────────────────────────────────

  const canMove = useMemo(() => {
    if (selectedIds.size === 0) return false;
    if (!destinationCase) return false;
    if (applying) return false;

    for (const id of selectedIds) {
      if (commentActivityMap[id] == null) return false;
    }

    return true;
  }, [selectedIds, destinationCase, applying, commentActivityMap]);

  // Count of selected comments with unmapped activities
  const unmappedSelectedCount = useMemo(() => {
    let count = 0;
    for (const id of selectedIds) {
      if (commentActivityMap[id] == null) count++;
    }
    return count;
  }, [selectedIds, commentActivityMap]);

  // ─── Handlers: Customer ──────────────────────────────

  const handleSelectCustomer = useCallback(
    (cust: SelectedCustomer | null) => {
      setCustomer(cust);
      // Clear everything downstream
      setSourceCase(null);
      setComments([]);
      setSelectedIds(new Set());
      setEmployeeFilter(null);
      setDestinationCase(null);
      setDestinationActivities([]);
      setCommentActivityMap({});
      setFailedCommentIds([]);
      setProgress({ done: 0, total: 0, failed: 0 });
      clearDeletability();
    },
    [clearDeletability],
  );

  // ─── Handlers: Source Case ───────────────────────────

  const handleSelectSourceCase = useCallback(
    async (caseId: number | null) => {
      // Clear downstream
      setComments([]);
      setSelectedIds(new Set());
      setEmployeeFilter(null);
      setCommentActivityMap({});
      setFailedCommentIds([]);
      setProgress({ done: 0, total: 0, failed: 0 });
      clearDeletability();

      if (!caseId || !customer) {
        setSourceCase(null);
        return;
      }

      try {
        setLoadingComments(true);
        const rawCase = await getCase(caseId);
        const typedCase = convertToCANtrackCase(rawCase);

        // ReadCase answers 200 {} for ids that do not exist, so the converted
        // id/customerID come back NaN. Guard before the customer check, which
        // would otherwise read NaN !== customer.id as a customer mismatch.
        if (!Number.isFinite(typedCase.id)) {
          notifyError(`Case ${caseId} was not found.`);
          setSourceCase(null);
          return;
        }

        // Validate customer matches
        if (typedCase.customerID !== customer.id) {
          notifyError(
            `Case ${caseId} belongs to a different customer (${typedCase.customerName}). Select a case for ${customer.name}.`,
          );
          setSourceCase(null);
          return;
        }

        setSourceCase(typedCase);

        // Deletability check (button renders `loading` meanwhile; comments fetch in parallel)
        void refreshDeletability(typedCase.id);

        // Fetch comments
        const rawComments = await getAllCommentsByCaseId(caseId);
        const typedComments = rawComments.map(convertToCANtrackComment);
        setComments(typedComments);

        if (typedComments.length === 0) {
          notifyWarning('No activities found for this case.');
        }

        // If destination case already selected, re-run auto-mapping
        if (destinationCase && destinationActivities.length > 0) {
          try {
            const { commentActivityMap: newMap } = await autoMapActivities(
              typedComments,
              destinationActivities,
            );
            setCommentActivityMap(newMap);
          } catch {
            setCommentActivityMap({});
          }
        }
      } catch (error) {
        console.error('[useCaseManagement] Failed to load source case:', error);
        notifyError('Failed to load case. Please try again.');
        setSourceCase(null);
      } finally {
        setLoadingComments(false);
      }
    },
    [
      customer,
      destinationCase,
      destinationActivities,
      notifyError,
      notifyWarning,
      clearDeletability,
      refreshDeletability,
    ],
  );

  // ─── Handlers: Destination Case ──────────────────────

  const handleSelectDestinationCase = useCallback(
    async (caseId: number | null) => {
      // Clear destination state
      setDestinationActivities([]);
      setCommentActivityMap({});
      setFailedCommentIds([]);
      setProgress({ done: 0, total: 0, failed: 0 });

      if (!caseId || !customer) {
        setDestinationCase(null);
        return;
      }

      // Validate: source != destination
      if (sourceCase && caseId === sourceCase.id) {
        notifyError('Destination case cannot be the same as the source case.');
        return;
      }

      try {
        setLoadingDestActivities(true);
        const rawCase = await getCase(caseId);
        const typedCase = convertToCANtrackCase(rawCase);

        // ReadCase answers 200 {} for ids that do not exist, so the converted
        // id/customerID come back NaN. Guard before the customer check, which
        // would otherwise read NaN !== customer.id as a customer mismatch.
        if (!Number.isFinite(typedCase.id)) {
          notifyError(`Case ${caseId} was not found.`);
          return;
        }

        // Validate customer matches
        if (typedCase.customerID !== customer.id) {
          notifyError(
            `Case ${caseId} belongs to a different customer (${typedCase.customerName}). Select a case for ${customer.name}.`,
          );
          return;
        }

        // Validate destination case has a deliverable
        if (!typedCase.gpd.deliverable.id || typedCase.gpd.deliverable.id === 0) {
          notifyError(
            'The destination case does not have a GPD (Group/Project/Deliverable) configured. Please assign a GPD to the destination case first.',
          );
          return;
        }

        setDestinationCase(typedCase);

        // Fetch activities for the destination deliverable
        const rawActivities = await getActivities(
          typedCase.gpd.deliverable.id,
        );
        const destActs: DestActivity[] = rawActivities.map((a: any) => ({
          id: Number(a.Id),
          title: String(a.Title),
        }));
        setDestinationActivities(destActs);

        if (destActs.length === 0) {
          notifyWarning(
            'No activities found for the destination deliverable. Activities must be created before moving.',
          );
          return;
        }

        // Auto-map activities
        if (comments.length > 0) {
          try {
            const { commentActivityMap: newMap } = await autoMapActivities(
              comments,
              destActs,
            );
            setCommentActivityMap(newMap);
          } catch (error) {
            console.error(
              '[useCaseManagement] Auto-mapping failed:',
              error,
            );
            setCommentActivityMap({});
          }
        }
      } catch (error) {
        console.error(
          '[useCaseManagement] Failed to load destination case:',
          error,
        );
        notifyError('Failed to load destination case. Please try again.');
        setDestinationCase(null);
      } finally {
        setLoadingDestActivities(false);
      }
    },
    [customer, sourceCase, comments, notifyError, notifyWarning],
  );

  // ─── Handlers: Per-row activity override ─────────────

  const handleSetCommentActivity = useCallback(
    (commentId: number, activityId: number) => {
      setCommentActivityMap((prev) => ({
        ...prev,
        [commentId]: activityId,
      }));
    },
    [],
  );

  // ─── Handlers: Employee Filter ───────────────────────

  const handleEmployeeFilter = useCallback(
    (consultantId: number | null) => {
      setEmployeeFilter(consultantId);
      // Note: selectedIds are NOT cleared when filter changes
    },
    [],
  );

  // ─── Handlers: Selection ─────────────────────────────

  const handleToggleSelect = useCallback(
    (commentId: number) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(commentId)) {
          next.delete(commentId);
        } else {
          next.add(commentId);
        }
        return next;
      });
    },
    [],
  );

  const handleToggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        // Deselect all visible
        for (const c of filteredComments) {
          next.delete(c.id);
        }
      } else {
        // Select all visible (additive to existing selection)
        for (const c of filteredComments) {
          next.add(c.id);
        }
      }
      return next;
    });
  }, [allVisibleSelected, filteredComments]);

  // ─── Handlers: Move Execution ────────────────────────

  const handleMoveComments = useCallback(async () => {
    if (!destinationCase || !customer || selectedIds.size === 0) return;

    // Final validation
    const unmapped: number[] = [];
    for (const id of selectedIds) {
      if (commentActivityMap[id] == null) unmapped.push(id);
    }
    if (unmapped.length > 0) {
      notifyError(
        `${unmapped.length} selected activity(ies) do not have a destination activity mapped.`,
      );
      return;
    }

    // Build activity title lookup
    const activityTitleLookup = new Map<number, string>();
    for (const a of destinationActivities) {
      activityTitleLookup.set(a.id, a.title);
    }

    // Get selected comments in their current order
    const selectedComments = comments.filter((c) => selectedIds.has(c.id));
    const total = selectedComments.length;

    // Start execution
    setApplying(true);
    applyingRef.current = true;
    setProgress({ done: 0, total, failed: 0 });
    setFailedCommentIds([]);

    let successCount = 0;
    let failCount = 0;
    const failedIds: number[] = [];

    for (let i = 0; i < selectedComments.length; i++) {
      const comment = selectedComments[i];
      const destActivityId = commentActivityMap[comment.id]!;
      const destActivityTitle =
        activityTitleLookup.get(destActivityId) ?? '';

      const updatedComment: CANtrackComment = {
        ...comment,
        caseID: destinationCase.id,
        caseTitle: destinationCase.title,
        customerID: customer.id,
        customerName: customer.name,
        gpda: {
          group: {
            id: destinationCase.gpd.group.id,
            title: destinationCase.gpd.group.title,
          },
          project: {
            id: destinationCase.gpd.project.id,
            title: destinationCase.gpd.project.title,
          },
          deliverable: {
            id: destinationCase.gpd.deliverable.id,
            title: destinationCase.gpd.deliverable.title,
          },
          activity: {
            id: destActivityId,
            title: destActivityTitle,
          },
        },
      };

      try {
        await updateComment(updatedComment);
        successCount++;
      } catch (error) {
        console.error(
          `[useCaseManagement] Failed to move activity "${comment.title}" (ID: ${comment.id}):`,
          error,
        );
        failCount++;
        failedIds.push(comment.id);
      }

      // Update progress after each comment
      setProgress({
        done: i + 1,
        total,
        failed: failCount,
      });
    }

    // ─── Completion ──────────────────────────────────

    setFailedCommentIds(failedIds);

    if (failCount === 0) {
      notifySuccess(
        `Moved ${successCount} activity(ies) successfully.`,
      );
    } else if (successCount === 0) {
      notifyError(
        `All ${failCount} activity(ies) failed to move. Please try again.`,
      );
    } else {
      // Partial failure
      const failedTitles = comments
        .filter((c) => failedIds.includes(c.id))
        .map((c) => c.title || `ID ${c.id}`)
        .slice(0, 5);

      const moreCount = failedIds.length - failedTitles.length;
      const failedList =
        failedTitles.join(', ') +
        (moreCount > 0 ? `, and ${moreCount} more` : '');

      notifyError(
        `Moved ${successCount}, failed ${failCount} activity(ies). Failed: ${failedList}`,
      );
    }

    // Refresh source comments (re-fetch to see what's left)
    try {
      if (sourceCase) {
        const rawComments = await getAllCommentsByCaseId(sourceCase.id);
        const typedComments = rawComments.map(convertToCANtrackComment);
        setComments(typedComments);
      }
    } catch (error) {
      console.error(
        '[useCaseManagement] Failed to refresh source comments:',
        error,
      );
    }

    // Re-check deletability: a fully drained source flips grey -> red/orange
    // with no user action (design §6). Covers full AND partial moves.
    if (sourceCase) {
      await refreshDeletability(sourceCase.id);
    }

    // Clear selection (only successfully moved ones)
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of prev) {
        if (!failedIds.includes(id)) {
          next.delete(id);
        }
      }
      return next;
    });

    // Reset applying state
    setApplying(false);
    applyingRef.current = false;
  }, [
    destinationCase,
    customer,
    selectedIds,
    commentActivityMap,
    destinationActivities,
    comments,
    sourceCase,
    notifySuccess,
    notifyError,
    refreshDeletability,
  ]);

  // ─── Handlers: Retry Failed ──────────────────────────

  const handleRetryFailed = useCallback(() => {
    if (failedCommentIds.length === 0) return;
    setSelectedIds(new Set(failedCommentIds));
    setFailedCommentIds([]);
    setProgress({ done: 0, total: 0, failed: 0 });
  }, [failedCommentIds]);

  // ─── Return ──────────────────────────────────────────

  const state: CaseManagementState = {
    customer,
    sourceCase,
    comments,
    loadingComments,
    employeeFilter,
    filteredComments,
    selectedIds,
    destinationCase,
    destinationActivities,
    loadingDestActivities,
    commentActivityMap,
    applying,
    progress,
    failedCommentIds,
    deletability,
    loadingDeletability,
    deleting,
    sourceResetSignal,
  };

  const handler: CaseManagementHandler = {
    onSelectCustomer: handleSelectCustomer,
    onSelectSourceCase: handleSelectSourceCase,
    onSelectDestinationCase: handleSelectDestinationCase,
    onSetCommentActivity: handleSetCommentActivity,
    onEmployeeFilter: handleEmployeeFilter,
    onToggleSelect: handleToggleSelect,
    onToggleSelectAll: handleToggleSelectAll,
    onMoveComments: handleMoveComments,
    onRetryFailed: handleRetryFailed,
    onDeleteCase: (confirmedAttachments: boolean) =>
      sourceCase
        ? executeDelete(sourceCase.id, confirmedAttachments)
        : Promise.resolve(false),
  };

  const derived: CaseManagementDerived = {
    employees,
    allVisibleSelected,
    someVisibleSelected,
    canMove,
    unmappedSelectedCount,
    deleteButtonState: deriveDeleteButtonState(
      Boolean(sourceCase),
      loadingDeletability,
      deletability,
    ),
    deleteBlockedTooltip: deriveDeleteBlockedTooltip(deletability),
  };

  return { state, handler, derived };
}

export default useCaseManagement;
